import { useTranslate } from '@jablotron/lib.spa-lang';
import { Link, useLocation, useRouter } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { Heading } from '~/components/heading/Heading';
import { getCatalog } from '~/entities/catalog/api';
import { Texts } from '~/texts/runtime';
import { CatalogSearchBox } from '~/widgets/catalog-search';
import { buildSidebarCategories, type SidebarCategory } from '~/widgets/catalog-sidebar';
import { useFooterContact } from '~/widgets/footer';

/**
 * Shared 404: unknown URLs (router default) and detail pages whose product, category or article doesn't exist.
 * One centred column: a product search, the product lines as a way back in, and the failed address with a
 * prefilled "report a broken link" e-mail to the partner line of the current language.
 */
export function NotFoundPage() {
  const t = useTranslate();
  const location = useLocation();
  const { origin } = useRouter();
  const contact = useFooterContact();
  const [productLines, setProductLines] = useState<SidebarCategory[]>([]);

  useEffect(() => {
    void getCatalog().then((loaded) => setProductLines(buildSidebarCategories(loaded)));
  }, []);

  // Shown without the protocol so it reads like what the visitor typed or clicked: the public address
  // (`/de/produkte/…`), not the internal path the route tree matched, and without the hash. Host and protocol
  // come from the router's origin (the browser's in the browser), never from `window`: this page also renders
  // on the server.
  const [publicPath] = location.publicHref.split('#');
  const { host, protocol } = new URL(origin);
  const displayUrl = `${host}${publicPath}`;
  const reportHref = `mailto:${contact.email}?subject=${encodeURIComponent(
    t(Texts.NotFoundReportSubject),
  )}&body=${encodeURIComponent(`${t(Texts.NotFoundReportBody)}\n${protocol}//${displayUrl}\n`)}`;

  return (
    <div data-testid="not-found-page" className="mx-auto max-w-screen-sm px-4 pb-16 pt-12 text-center sm:px-6 sm:pt-16">
      <div
        aria-hidden="true"
        className="mb-7 inline-flex size-24 -rotate-6 items-center justify-center rounded-[28px] bg-brand-yellow text-[34px] font-semibold tracking-tight text-brand-ink sm:size-28 sm:rounded-4xl sm:text-[40px]"
      >
        404
      </div>
      <Heading level={1} size="page">
        {t(Texts.NotFoundHeading)}
      </Heading>
      <p className="mb-0 mt-4 text-base leading-normal text-brand-muted sm:text-lg">{t(Texts.NotFoundLead)}</p>

      <CatalogSearchBox
        variant="hero"
        placeholder={Texts.NotFoundSearchPlaceholder}
        className="mx-auto mt-8 max-w-[600px] text-left"
      />

      {productLines.length > 0 && (
        <nav aria-label={t(Texts.NotFoundLinesHeading)} className="mt-7">
          <ul
            data-testid="not-found-lines"
            className="m-0 flex list-none flex-wrap items-center justify-center gap-x-5 gap-y-2 p-0 text-sm"
          >
            <li className="text-brand-subtle">{t(Texts.NotFoundLinesHeading)}:</li>
            {productLines.map((line) => (
              <li key={line.id}>
                <Link
                  to="/categories/$categoryId"
                  params={{ categoryId: line.slug }}
                  className="font-semibold text-brand-cyanText underline"
                >
                  {t(line.title)}
                </Link>
              </li>
            ))}
          </ul>
        </nav>
      )}

      <p className="mb-0 mt-16 text-small text-brand-subtle">
        {t(Texts.NotFoundInvalidAddress)}:{' '}
        <code
          data-testid="not-found-url"
          className="inline-block max-w-full break-all rounded-md bg-brand-surface px-2 py-1 font-mono"
        >
          {displayUrl}
        </code>
        {/* On a phone the report link gets a line of its own instead of wrapping after the address. */}
        <span aria-hidden="true" className="hidden sm:inline">
          {' '}
          ·{' '}
        </span>
        <a href={reportHref} className="mt-2 block text-brand-subtle underline sm:mt-0 sm:inline">
          {t(Texts.NotFoundReportBrokenLink)}
        </a>
      </p>
    </div>
  );
}
